import { useEffect } from 'react';

/**
 * usePageMeta - set document title and (optionally) meta description on mount.
 * Resets to the previous values on unmount.
 *
 * Pass a string (title only) or an object with `title` + `description`.
 *
 *   useDocumentTitle('iExchangeWeb');
 *   // → <title>iExchangeWeb | iConnect</title>
 *
 *   useDocumentTitle({
 *     title: 'iExchangeWeb',
 *     description: 'Web-based EDI in days, not months. iConnect iExchangeWeb...',
 *   });
 *   // → <title>iExchangeWeb | iConnect</title>
 *   //   <meta name="description" content="Web-based EDI in days...">
 */
const DEFAULT_SUFFIX = 'iConnect';

const setMeta = (name, content) => {
  if (!content) return null;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  const previous = el.getAttribute('content');
  el.setAttribute('content', content);
  return () => {
    if (previous !== null) el.setAttribute('content', previous);
  };
};

const setOgTag = (property, content) => {
  if (!content) return null;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  const previous = el.getAttribute('content');
  el.setAttribute('content', content);
  return () => {
    if (previous !== null) el.setAttribute('content', previous);
  };
};

export default function useDocumentTitle(input, suffix = DEFAULT_SUFFIX) {
  const title = typeof input === 'string' ? input : input?.title;
  const description = typeof input === 'object' ? input.description : null;

  useEffect(() => {
    if (!title) return;
    const previousTitle = document.title;
    const fullTitle = suffix ? `${title} | ${suffix}` : title;
    document.title = fullTitle;

    // Sync meta description, og:title, og:description, twitter:title/description
    const restorers = [
      setMeta('description', description),
      setOgTag('og:title', fullTitle),
      setOgTag('og:description', description),
      setMeta('twitter:title', fullTitle),
      setMeta('twitter:description', description),
    ].filter(Boolean);

    return () => {
      document.title = previousTitle;
      restorers.forEach((fn) => fn());
    };
  }, [title, description, suffix]);
}
